import { uid } from "./dials";

export type LyricPackStatus = "ready" | "making" | "done" | "error";

export type LyricPack = {
  id: string;
  title: string;
  lyrics: string;
  source: string;
  status: LyricPackStatus;
  error?: string;
};

const PACK_KEY = "musicforge.lyricPacks.v1";
const MAX_PACKS = 80;
const SKIP_NAME = /^(duration|album|manifest|chat|compare)\b/i;
const OK_EXT = /\.(txt|lrc|md|json)$/i;
const SONG_SPLIT = /\n(?:---+|\*\*\*+|===+)\s*\n/;
const TITLE_LINE = /^(?:title|song)\s*[:\-]\s*(.+)$/i;

export function loadLyricPacks(): LyricPack[] {
  try {
    const raw = localStorage.getItem(PACK_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as LyricPack[];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((p) => p && typeof p.lyrics === "string" && p.lyrics.trim())
      .slice(0, MAX_PACKS)
      .map((p) => ({
        ...p,
        status: p.status === "making" ? "ready" : p.status || "ready",
      }));
  } catch {
    return [];
  }
}

export function saveLyricPacks(packs: LyricPack[]): void {
  try {
    localStorage.setItem(PACK_KEY, JSON.stringify(packs.slice(0, MAX_PACKS)));
  } catch {
    /* */
  }
}

export function fileStem(name: string): string {
  return name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || "Untitled";
}

function relativePath(file: File): string {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

export function titleFromFile(file: File): string {
  const parts = relativePath(file).split(/[/\\]/).filter(Boolean);
  const leaf = parts[parts.length - 1] || file.name;
  const folder = parts.length > 1 ? parts[parts.length - 2] : fileStem(leaf);
  const fromFolder = folder.replace(/[-_]+/g, " ").replace(/\blyrics\b/i, "").trim();
  if (fromFolder && !/^lyrics$/i.test(fromFolder)) return fromFolder;
  return fileStem(leaf);
}

export function isLyricFile(file: File): boolean {
  const leaf = (relativePath(file).split(/[/\\]/).pop() || file.name).toLowerCase();
  if (SKIP_NAME.test(leaf)) return false;
  return OK_EXT.test(leaf);
}

export function parseLyricText(text: string, fallbackTitle: string): { title: string; lyrics: string }[] {
  const chunks = text
    .replace(/^\uFEFF/, "")
    .split(SONG_SPLIT)
    .map((s) => s.trim())
    .filter(Boolean);
  return chunks
    .map((chunk, i) => {
      const lines = chunk.split(/\r?\n/);
      let title = chunks.length > 1 ? `${fallbackTitle} ${i + 1}` : fallbackTitle;
      let start = 0;
      if (lines[0] && TITLE_LINE.test(lines[0])) {
        title = lines[0].replace(TITLE_LINE, "$1").trim() || title;
        start = 1;
      }
      return { title, lyrics: lines.slice(start).join("\n").trim() };
    })
    .filter((p) => p.lyrics);
}

function packsFromJson(raw: string, fallbackTitle: string, source: string): LyricPack[] {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === "object" && Array.isArray((data as { songs?: unknown }).songs)
      ? (data as { songs: unknown[] }).songs
      : data && typeof data === "object"
        ? [data]
        : [];
  const out: LyricPack[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const rec = row as { title?: unknown; name?: unknown; lyrics?: unknown; text?: unknown };
    const lyrics = String(rec.lyrics || rec.text || "").trim();
    if (!lyrics) continue;
    out.push({
      id: uid("lyric"),
      title: String(rec.title || rec.name || fallbackTitle).trim() || fallbackTitle,
      lyrics,
      source,
      status: "ready",
    });
  }
  return out;
}

export async function parseLyricFiles(files: File[]): Promise<LyricPack[]> {
  const out: LyricPack[] = [];
  for (const file of files) {
    if (!isLyricFile(file)) continue;
    const title = titleFromFile(file);
    const text = await file.text();
    if (/\.json$/i.test(file.name)) {
      out.push(...packsFromJson(text, title, relativePath(file)));
      continue;
    }
    for (const song of parseLyricText(text, title)) {
      out.push({
        id: uid("lyric"),
        title: song.title,
        lyrics: song.lyrics,
        source: relativePath(file),
        status: "ready",
      });
    }
    if (out.length >= MAX_PACKS) break;
  }
  return out.slice(0, MAX_PACKS);
}

export function mergeLyricPacks(cur: LyricPack[], add: LyricPack[]): LyricPack[] {
  const seen = new Set(cur.map((p) => `${p.title}\n${p.lyrics}`));
  const next = [...cur];
  for (const p of add) {
    const key = `${p.title}\n${p.lyrics}`;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(p);
    if (next.length >= MAX_PACKS) break;
  }
  return next;
}
